import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFonts } from "@/context/FontsContext";
import { useAuth } from "@/context/AuthContext";
import { Download, Save, Copy, ZoomIn, ZoomOut, Maximize2, ChevronDown, Undo2, Redo2, RotateCcw, RotateCw, SpellCheck } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { DebouncedInput } from "@/components/DebouncedField";
import { toast } from "sonner";
import ElementsList from "@/components/designer/ElementsList";
import PropertiesPanel from "@/components/designer/PropertiesPanel";
import DesignerCanvas from "@/components/designer/DesignerCanvas";
import { defaultAddedText, defaultAddedImage, defaultOverlay, uploadAndGetUrl, uploadLogoAndGetUrl, removeBgFromUrl, detectInheritedTransform, FIXED_FIELDS, TEXT_FIELDS, replaceWord } from "@/components/designer/designerUtils";

function SelectorSlot({ index, label, value, onChange, options, renderThumb, disabled, searchable, searchPlaceholder, noMatchText, footer }) {
  const [q, setQ] = useState("");
  const selected = options.find((o) => o.id === value);
  const filtered = searchable && q.trim() ? options.filter((o) => o.name.toLowerCase().includes(q.trim().toLowerCase())) : options;
  return (
    <div className="bhf-card flex min-w-[210px] flex-1 items-center gap-3 px-3.5 py-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-[10px] font-semibold text-slate-400">
        {selected && renderThumb ? renderThumb(selected) : index}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{index}. {label}</div>
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="mt-0.5 h-7 border-0 px-0 shadow-none focus:ring-0" data-testid={`selector-${label.toLowerCase()}`}>
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {searchable && (
              <div className="sticky top-0 z-10 bg-white p-1.5">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder={searchPlaceholder} className="h-8" data-testid={`selector-${label.toLowerCase()}-search`} />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto">
              {filtered.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              {searchable && filtered.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">{noMatchText}</div>}
            </div>
            {footer}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function ColorCardDesigner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reload: reloadFonts } = useFonts();
  const { isAdmin } = useAuth();

  const [design, setDesign] = useState(null);
  const [product, setProduct] = useState(null);
  const [template, setTemplate] = useState(null);
  const [brand, setBrand] = useState(null);
  const [customContent, setCustomContent] = useState({});
  const [customer, setCustomer] = useState("");
  const [selection, setSelection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detectingText, setDetectingText] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [showSafeArea, setShowSafeArea] = useState(true);
  const [drawingLogo, setDrawingLogo] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [spellResults, setSpellResults] = useState({});
  const [spellActive, setSpellActive] = useState(false);
  const [spellChecking, setSpellChecking] = useState(false);
  const spellActiveRef = useRef(false);
  const recheckTimer = useRef(null);
  const [textResetKey, setTextResetKey] = useState(0);

  const [productsList, setProductsList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [customCanvasOpen, setCustomCanvasOpen] = useState(false);
  const [canvasForm, setCanvasForm] = useState({ name: "", widthMM: 210, heightMM: 297, bleedMM: 3 });
  const [creatingCanvas, setCreatingCanvas] = useState(false);
  const [brandsList, setBrandsList] = useState([]);
  const [selProductId, setSelProductId] = useState("");
  const [selTemplateId, setSelTemplateId] = useState("");
  const [selBrandId, setSelBrandId] = useState("");

  const stateRef = useRef({ customContent: {}, customer: "", productId: "", brandId: "" });
  const saveTimer = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  useEffect(() => {
    stateRef.current = { customContent, customer, productId: selProductId, brandId: selBrandId };
  }, [customContent, customer, selProductId, selBrandId]);

  useEffect(() => {
    api.get("/products", { params: { status: "Active" } }).then(({ data }) => setProductsList(data));
    api.get("/templates", { params: { status: "Active" } }).then(({ data }) => setTemplatesList(data));
    api.get("/brands", { params: { status: "Active" } }).then(({ data }) => setBrandsList(data));
  }, []);

  const loadDesign = useCallback(async (designId) => {
    const { data } = await api.get(`/designs/${designId}`);
    setDesign(data);
    setProduct(data.product);
    setTemplate(data.template);
    setBrand(data.brand);
    setCustomContent(data.customContent || {});
    setCustomer(data.customer || "");
    setSelProductId(data.productId || "");
    setSelBrandId(data.brandId || "");
    setSelTemplateId(data.templateId || "");
    setSelection(null);
    setRotation(0);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setSpellResults({});
    setSpellActive(false);
    spellActiveRef.current = false;
    reloadFonts();
  }, [reloadFonts]);

  useEffect(() => {
    if (!id) { setDesign(null); setProduct(null); setTemplate(null); setBrand(null); setCustomContent({}); setSelection(null); return; }
    if (design && design.id === id) return;
    loadDesign(id);
  }, [id, design, loadDesign]);

  useEffect(() => {
    if (!id && selProductId && selTemplateId && selBrandId && !generating) {
      (async () => {
        setGenerating(true);
        try {
          const { data } = await api.post("/designs/generate", { productId: selProductId, templateId: selTemplateId, brandId: selBrandId, customer });
          setDesign(data);
          setProduct(productsList.find((p) => p.id === selProductId));
          setTemplate(templatesList.find((t) => t.id === selTemplateId));
          setBrand(brandsList.find((b) => b.id === selBrandId));
          setCustomContent(data.customContent || {});
          navigate(`/designer/${data.id}`, { replace: true });
          toast.success(t("designer.generatedAs", { n: data.designNumber }));
        } catch (e) {
          toast.error(t("designer.generateFailed"));
        } finally {
          setGenerating(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selProductId, selTemplateId, selBrandId]);

  const scheduleSave = () => {
    if (!design?.id) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put(`/designs/${design.id}`, stateRef.current);
        setDesign(data);
      } catch (e) {
        toast.error(t("designer.autosaveFailed"));
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const commitCustomContent = (next, { history = true } = {}) => {
    if (history) {
      undoStackRef.current.push(customContent);
      if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
    }
    setCustomContent(next);
    stateRef.current = { ...stateRef.current, customContent: next };
    scheduleSave();
  };

  const applyRemoteCustomContent = (nextCC) => {
    undoStackRef.current.push(customContent);
    if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setCustomContent(nextCC);
    stateRef.current = { ...stateRef.current, customContent: nextCC };
  };

  const handleUndo = useCallback(() => {
    if (!undoStackRef.current.length) return;
    const cur = stateRef.current.customContent;
    const prev = undoStackRef.current.pop();
    redoStackRef.current.push(cur);
    setCustomContent(prev);
    stateRef.current = { ...stateRef.current, customContent: prev };
    scheduleSave();
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    setSelection(null);
  }, []);

  const handleRedo = useCallback(() => {
    if (!redoStackRef.current.length) return;
    const cur = stateRef.current.customContent;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push(cur);
    setCustomContent(next);
    stateRef.current = { ...stateRef.current, customContent: next };
    scheduleSave();
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    setSelection(null);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleUndo, handleRedo]);

  const patchField = (key, value) => {
    const next = { ...customContent };
    if (value === undefined) delete next[key];
    else next[key] = value;
    commitCustomContent(next);
    if (TEXT_FIELDS.includes(key)) scheduleRecheck(`fixed:${key}`, value);
  };
  const patchNested = (group, key, value) => {
    commitCustomContent({ ...customContent, [group]: { ...(customContent[group] || {}), [key]: value } });
  };
  const patchPosition = (key, patch) => {
    commitCustomContent({ ...customContent, positions: { ...(customContent.positions || {}), [key]: { ...(customContent.positions?.[key] || {}), ...patch } } });
  };
  const patchLogoAndBox = (logoUrl, box) => {
    const next = { ...customContent, logoUrlOverride: logoUrl };
    if (box) next.positions = { ...(customContent.positions || {}), logo: box };
    commitCustomContent(next);
  };

  const addText = () => {
    const arr = [...(customContent.addedTexts || []), defaultAddedText()];
    commitCustomContent({ ...customContent, addedTexts: arr });
    setSelection({ kind: "text", key: arr.length - 1 });
  };
  const addImage = () => {
    const arr = [...(customContent.addedImages || []), defaultAddedImage()];
    commitCustomContent({ ...customContent, addedImages: arr });
    setSelection({ kind: "image", key: arr.length - 1 });
  };
  const updateAddedText = (idx, patch) => {
    const arr = [...(customContent.addedTexts || [])];
    arr[idx] = { ...arr[idx], ...patch };
    commitCustomContent({ ...customContent, addedTexts: arr });
    if ("text" in patch) scheduleRecheck(`text:${idx}`, patch.text);
  };
  const removeAddedText = (idx) => {
    const arr = [...(customContent.addedTexts || [])];
    arr.splice(idx, 1);
    commitCustomContent({ ...customContent, addedTexts: arr });
    setSelection(null);
  };
  const updateAddedImage = (idx, patch) => {
    const arr = [...(customContent.addedImages || [])];
    arr[idx] = { ...arr[idx], ...patch };
    commitCustomContent({ ...customContent, addedImages: arr });
  };
  const removeAddedImage = (idx) => {
    const arr = [...(customContent.addedImages || [])];
    arr.splice(idx, 1);
    commitCustomContent({ ...customContent, addedImages: arr });
    setSelection(null);
  };

  const addOverlay = (type) => {
    const base = defaultOverlay(type);
    const inherited = detectInheritedTransform(customContent.overlays, base);
    const arr = [...(customContent.overlays || []), inherited ? { ...base, transform: inherited } : base];
    commitCustomContent({ ...customContent, overlays: arr });
    setSelection({ kind: "overlay", key: arr.length - 1 });
  };
  const toggleDrawLogo = () => setDrawingLogo((v) => !v);
  const finishDrawLogo = async (rect) => {
    setDrawingLogo(false);
    if (!design?.id) return;
    try {
      const { data } = await api.post(`/designs/${design.id}/extract-logo`, rect);
      setDesign(data);
      applyRemoteCustomContent(data.customContent || {});
      setSelection({ kind: "overlay", key: (data.customContent?.overlays || []).length - 1 });
      toast.success(t("designer.aiLogoDetected"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("designer.aiLogoFailed"));
    }
  };
  const updateOverlay = (idx, patch) => {
    const arr = [...(customContent.overlays || [])];
    arr[idx] = { ...arr[idx], ...patch };
    commitCustomContent({ ...customContent, overlays: arr });
    if ("text" in patch && arr[idx].type === "text") scheduleRecheck(`overlay:${idx}`, patch.text);
  };
  const removeOverlay = (idx) => {
    const arr = [...(customContent.overlays || [])];
    arr.splice(idx, 1);
    commitCustomContent({ ...customContent, overlays: arr });
    setSelection(null);
  };

  const uploadFile = (file) => uploadAndGetUrl(api, file);
  const uploadLogoFile = (file) => uploadLogoAndGetUrl(api, file);
  const removeBgFile = (url) => removeBgFromUrl(api, url);

  const handleAutoDetectText = async () => {
    if (!design?.id || detectingText) return;
    setDetectingText(true);
    try {
      const { data } = await api.post(`/designs/${design.id}/auto-detect-text`);
      setDesign(data);
      applyRemoteCustomContent(data.customContent || {});
      toast.success(t("designer.aiTextDetected"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("designer.aiTextFailed"));
    } finally {
      setDetectingText(false);
    }
  };

  const onReplaceBackgroundFile = async (file) => {
    if (!design?.id) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/designs/${design.id}/replace-background`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDesign(data);
      applyRemoteCustomContent(data.customContent || {});
      toast.success(t("designer.bgReplaced"));
    } catch (e) {
      toast.error(t("designer.bgReplaceFailed"));
    }
  };

  const handleCustomerChange = (v) => {
    setCustomer(v);
    stateRef.current = { ...stateRef.current, customer: v };
    scheduleSave();
  };

  const handleSaveDraft = async () => {
    if (!design?.id) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/designs/${design.id}`, { ...stateRef.current, status: "Draft", renderPreview: true });
      setDesign(data);
      toast.success(t("designer.draftSaved"));
    } catch (e) {
      toast.error(t("designer.saveDraftFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!design?.id) return;
    try {
      const { data } = await api.post(`/designs/${design.id}/version`, { ...stateRef.current, status: "Draft" });
      setDesign(data);
      setCustomContent(data.customContent || {});
      navigate(`/designer/${data.id}`, { replace: true });
      toast.success(t("designer.savedAs", { n: data.designNumber, v: data.version }));
    } catch (e) {
      toast.error(t("designer.saveVersionFailed"));
    }
  };

  const handleExport = async (format) => {
    if (!design?.id) return;
    try {
      const resp = await api.get(`/designs/${design.id}/export`, { params: { format }, responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${design.designNumber}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(t("designer.exportFailed"));
    }
  };

  useEffect(() => {
    if (!drawingLogo) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawingLogo(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawingLogo]);

  const gatherTextBlocks = () => {
    const blocks = [];
    if (design?.sourceType === "ExistingCard") {
      (customContent.overlays || []).forEach((o, i) => {
        if (o.type === "text" && String(o.text || "").trim()) blocks.push({ id: `overlay:${i}`, text: o.text });
      });
    } else {
      const productKeyMap = { productTitle: "productName" };
      TEXT_FIELDS.forEach((f) => {
        if (!template?.elementConfig?.[f]) return;
        const val = customContent[f] ?? product?.[productKeyMap[f] || f] ?? "";
        if (String(val).trim()) blocks.push({ id: `fixed:${f}`, text: String(val) });
      });
      (customContent.addedTexts || []).forEach((t, i) => {
        if (String(t.text || "").trim()) blocks.push({ id: `text:${i}`, text: t.text });
      });
    }
    return blocks;
  };

  const scheduleRecheck = (key, text) => {
    if (!spellActiveRef.current) return;
    clearTimeout(recheckTimer.current);
    const t = String(text ?? "");
    recheckTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/spell-check", { blocks: [{ id: key, text: t }] });
        setSpellResults((prev) => {
          const next = { ...prev };
          if (data.results && data.results[key]) next[key] = data.results[key];
          else delete next[key];
          return next;
        });
      } catch (e) { /* silent for live check */ }
    }, 700);
  };

  const handleToggleSpellCheck = async () => {
    if (spellActive) {
      setSpellActive(false);
      spellActiveRef.current = false;
      setSpellResults({});
      return;
    }
    setSpellActive(true);
    spellActiveRef.current = true;
    const blocks = gatherTextBlocks();
    if (!blocks.length) { setSpellResults({}); toast.info(t("designer.noTextToCheck")); return; }
    setSpellChecking(true);
    try {
      const { data } = await api.post("/spell-check", { blocks });
      setSpellResults(data.results || {});
      const n = Object.values(data.results || {}).reduce((s, r) => s + (r.issues?.length || 0), 0);
      toast.success(n ? t("designer.spellFound", { n }) : t("designer.spellNone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("designer.spellFailed"));
    } finally {
      setSpellChecking(false);
    }
  };

  const totalSpellIssues = Object.values(spellResults).reduce((s, r) => s + (r.issues?.length || 0), 0);
  const selectionSpellKey = selection ? (
    selection.kind === "fixed" ? `fixed:${selection.key}` :
    selection.kind === "text" ? `text:${selection.key}` :
    selection.kind === "overlay" ? `overlay:${selection.key}` : null
  ) : null;
  const selectedSpellResult = selectionSpellKey ? spellResults[selectionSpellKey] : null;

  const currentSelectionText = () => {
    if (!selection) return null;
    if (selection.kind === "fixed") {
      const productKeyMap = { productTitle: "productName" };
      return String(customContent[selection.key] ?? product?.[productKeyMap[selection.key] || selection.key] ?? "");
    }
    if (selection.kind === "text") return String((customContent.addedTexts || [])[selection.key]?.text ?? "");
    if (selection.kind === "overlay") return String((customContent.overlays || [])[selection.key]?.text ?? "");
    return null;
  };
  const commitSelectionText = (newText) => {
    if (!selection) return;
    if (selection.kind === "fixed") patchField(selection.key, newText);
    else if (selection.kind === "text") updateAddedText(selection.key, { text: newText });
    else if (selection.kind === "overlay") updateOverlay(selection.key, { text: newText });
    setTextResetKey((t) => t + 1);
  };
  const dropSpellIssue = (word, suggestion) => {
    if (!selectionSpellKey) return;
    setSpellResults((prev) => {
      if (!prev[selectionSpellKey]) return prev;
      const remaining = (prev[selectionSpellKey].issues || []).filter((i) => !(i.word === word && i.suggestion === suggestion));
      const next = { ...prev };
      if (remaining.length) next[selectionSpellKey] = { ...prev[selectionSpellKey], issues: remaining };
      else delete next[selectionSpellKey];
      return next;
    });
  };
  const applySpellFix = (word, suggestion) => {
    const t = currentSelectionText();
    if (t == null) return;
    commitSelectionText(replaceWord(t, word, suggestion));
    dropSpellIssue(word, suggestion);
  };
  const applySpellFixAll = (issues) => {
    let t = currentSelectionText();
    if (t == null) return;
    for (const iss of issues || []) t = replaceWord(t, iss.word, iss.suggestion);
    commitSelectionText(t);
    if (selectionSpellKey) setSpellResults((prev) => { const next = { ...prev }; delete next[selectionSpellKey]; return next; });
  };

  const handleCreateCanvas = async () => {
    const w = Number(canvasForm.widthMM), h = Number(canvasForm.heightMM), b = Number(canvasForm.bleedMM);
    if (!(w > 0) || !(h > 0)) { toast.error(t("designer.canvasFailed")); return; }
    setCreatingCanvas(true);
    try {
      const payload = {
        templateCode: `CUSTOM-${Date.now()}`,
        templateName: canvasForm.name?.trim() || `Custom ${w}x${h}`,
        category: "Other",
        orientation: w >= h ? "Landscape" : "Portrait",
        widthMM: w, heightMM: h, bleedMM: b >= 0 ? b : 0,
        elementConfig: {},
      };
      const { data } = await api.post("/templates", payload);
      const { data: list } = await api.get("/templates");
      setTemplatesList(list);
      setSelTemplateId(data.id);
      setCustomCanvasOpen(false);
      toast.success(t("designer.canvasCreated"));
    } catch (e) {
      toast.error(t("designer.canvasFailed"));
    } finally {
      setCreatingCanvas(false);
    }
  };

  const mode = design?.sourceType === "ExistingCard" ? "B" : "A";
  const bleedXPct = template ? (template.bleedMM / template.widthMM) * 100 : 0;
  const bleedYPct = template ? (template.bleedMM / template.heightMM) * 100 : 0;

  const boxes = [];
  if (mode === "A" && template) {
    FIXED_FIELDS.forEach(({ key, label }) => {
      const base = template.elementConfig?.[key];
      if (!base) return;
      const override = customContent.positions?.[key];
      boxes.push({ kind: "fixed", key, label, testId: key, x: override?.x ?? base.x, y: override?.y ?? base.y, width: override?.width ?? base.width, height: override?.height ?? base.height });
    });
    (customContent.addedTexts || []).forEach((t, i) => boxes.push({ kind: "text", key: i, label: `Added Text ${i + 1}`, testId: `text-${i}`, x: t.x, y: t.y, width: t.width, height: t.height }));
    (customContent.addedImages || []).forEach((im, i) => boxes.push({ kind: "image", key: i, label: `Added Image ${i + 1}`, testId: `image-${i}`, x: im.x, y: im.y, width: im.width, height: im.height }));
  } else if (mode === "B") {
    (customContent.overlays || []).forEach((o, i) => boxes.push({ kind: "overlay", key: i, label: `${o.type} ${i + 1}`, testId: `overlay-${i}`, x: o.x, y: o.y, width: o.width, height: o.height }));
  }

  const handleSelectBox = (box) => setSelection({ kind: box.kind, key: box.key });
  const handleCommitBox = (box, patch) => {
    if (box.kind === "fixed") patchPosition(box.key, patch);
    else if (box.kind === "text") updateAddedText(box.key, patch);
    else if (box.kind === "image") updateAddedImage(box.key, patch);
    else if (box.kind === "overlay") updateOverlay(box.key, patch);
  };

  return (
    <div className="flex h-screen flex-col" data-testid="designer-page">
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-lg font-700 text-slate-900">{t("designer.title")}</h1>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${mode === "A" ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-600"}`}>{mode === "A" ? t("designer.modeANew") : t("designer.modeBEdit")}</span>
          {design?.isStandardCard && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600" data-testid="standard-card-badge">
              {t("designer.standardBadge")} · {design.standardCardCode}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          {design && <span className="font-mono-bhf text-xs text-slate-400" data-testid="design-number-label">{design.designNumber} · V{design.version}</span>}
          {saving && <span className="text-xs text-indigo-500" data-testid="autosave-indicator">{t("common.saving")}</span>}
          <div className="flex items-center gap-1 border-r border-slate-200 pr-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canUndo} onClick={handleUndo} title={t("common.back") + " (Ctrl+Z)"} data-testid="undo-button"><Undo2 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canRedo} onClick={handleRedo} title="Ctrl+Y" data-testid="redo-button"><Redo2 className="h-4 w-4" /></Button>
          </div>
          <Button
            variant="outline" size="sm"
            className={`gap-1.5 ${spellActive ? "border-red-300 bg-red-50 text-red-600" : ""}`}
            disabled={!design || spellChecking}
            onClick={handleToggleSpellCheck}
            title={t("designer.spellCheck")}
            data-testid="spell-check-button"
          >
            <SpellCheck className="h-3.5 w-3.5" />
            {spellChecking ? t("designer.spellChecking") : spellActive ? `${t("designer.spellCheck")} · ${totalSpellIssues}` : t("designer.spellCheck")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" disabled={!design} data-testid="export-menu-button"><Download className="h-3.5 w-3.5" /> {t("designer.export")} <ChevronDown className="h-3 w-3" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("png")} data-testid="export-png-item">{t("designer.exportPng")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf-item">{t("designer.exportPdf")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="gap-1.5" disabled={!design} onClick={handleSaveDraft} data-testid="save-draft-button"><Save className="h-3.5 w-3.5" /> {t("designer.saveDraft")}</Button>
          <Button size="sm" className="bhf-btn-primary gap-1.5 border-0 text-white" disabled={!design} onClick={handleSaveVersion} data-testid="save-version-button"><Copy className="h-3.5 w-3.5" /> {t("designer.saveVersion")}</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-slate-200 bg-[#f6f7f9] px-6 py-3">
        {mode === "A" ? (
          <>
            <SelectorSlot index={1} label={t("designer.product")} value={selProductId} onChange={setSelProductId} searchable searchPlaceholder={t("designer.searchProduct")} noMatchText={t("designer.noMatch")} options={productsList.map((p) => ({ id: p.id, name: `${p.sku} · ${p.productName}` }))} renderThumb={(o) => { const p = productsList.find((x) => x.id === o.id); return p?.mainImage ? <img src={fileUrl(p.mainImage)} className="h-full w-full object-cover" /> : "1"; }} />
            <SelectorSlot index={2} label={t("designer.template")} value={selTemplateId} onChange={setSelTemplateId} disabled={!!design} searchable searchPlaceholder={t("designer.searchTemplate")} noMatchText={t("designer.noMatch")} options={templatesList.map((t) => ({ id: t.id, name: `${t.templateCode} · ${t.templateName}` }))} renderThumb={() => "T"}
              footer={!design && isAdmin ? (
                <div className="border-t border-slate-100 p-1">
                  <button type="button" onClick={() => setCustomCanvasOpen(true)} data-testid="custom-canvas-button" className="w-full rounded px-2 py-1.5 text-left text-xs font-medium text-indigo-600 hover:bg-indigo-50">
                    {t("designer.customCanvas")}
                  </button>
                </div>
              ) : null}
            />
            <SelectorSlot index={3} label={t("designer.brand")} value={selBrandId} onChange={setSelBrandId} options={brandsList.map((b) => ({ id: b.id, name: b.brandName }))} renderThumb={(o) => { const b = brandsList.find((x) => x.id === o.id); return b?.logos?.main ? <img src={fileUrl(b.logos.main)} className="h-full w-full object-contain p-1" /> : "3"; }} />

            <Dialog open={customCanvasOpen} onOpenChange={setCustomCanvasOpen}>
              <DialogContent data-testid="custom-canvas-dialog">
                <DialogHeader><DialogTitle>{t("designer.customCanvasTitle")}</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div><Label className="text-xs">{t("designer.canvasName")}</Label><Input className="mt-1" value={canvasForm.name} onChange={(e) => setCanvasForm({ ...canvasForm, name: e.target.value })} placeholder={t("designer.canvasNamePh")} data-testid="canvas-name-input" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">{t("designer.cw")}</Label><Input type="number" className="mt-1" value={canvasForm.widthMM} onChange={(e) => setCanvasForm({ ...canvasForm, widthMM: e.target.value })} data-testid="canvas-width-input" /></div>
                    <div><Label className="text-xs">{t("designer.ch")}</Label><Input type="number" className="mt-1" value={canvasForm.heightMM} onChange={(e) => setCanvasForm({ ...canvasForm, heightMM: e.target.value })} data-testid="canvas-height-input" /></div>
                    <div><Label className="text-xs">{t("designer.cbleed")}</Label><Input type="number" className="mt-1" value={canvasForm.bleedMM} onChange={(e) => setCanvasForm({ ...canvasForm, bleedMM: e.target.value })} data-testid="canvas-bleed-input" /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCustomCanvasOpen(false)}>{t("common.cancel")}</Button>
                  <Button className="bhf-btn-primary border-0 text-white" disabled={creatingCanvas} onClick={handleCreateCanvas} data-testid="create-canvas-submit">{creatingCanvas ? t("common.saving") : t("designer.createCanvas")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="bhf-card flex flex-1 items-center gap-6 px-4 py-3">
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">{t("designer.sourceFile")}</div><div className="text-sm text-slate-700">{design?.sourceFile?.fileName || "—"}</div></div>
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">{t("designer.fileType")}</div><div className="text-sm uppercase text-slate-700">{design?.sourceFile?.fileType || "—"}</div></div>
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">{t("designer.version")}</div><div className="text-sm text-slate-700">V{design?.version}</div></div>
          </div>
        )}
        <div className="bhf-card flex items-center gap-2 px-3.5 py-2.5">
          <span className="text-[10px] font-semibold uppercase text-slate-400">{t("designer.customer")}</span>
          <DebouncedInput key={design?.id || "new"} className="h-7 w-36 border-0 px-1.5 shadow-none focus-visible:ring-0" placeholder={t("designer.customerPlaceholder")} initialValue={customer} onCommit={handleCustomerChange} data-testid="customer-input" />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <ElementsList mode={mode} template={template} customContent={customContent} selection={selection} setSelection={setSelection}
          onAddText={addText} onAddImage={addImage} onAddOverlay={addOverlay}
          onRemoveText={removeAddedText} onRemoveImage={removeAddedImage} onRemoveOverlay={removeOverlay}
          onAutoDetectText={handleAutoDetectText} detectingText={detectingText}
          drawingLogo={drawingLogo} onToggleDrawLogo={toggleDrawLogo} />

        <div className="flex flex-1 flex-col overflow-hidden bg-[#eef0f3]">
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2">
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom((z) => Math.max(50, z - 10))} data-testid="zoom-out-button"><ZoomOut className="h-4 w-4" /></Button>
              <span className="w-10 text-center text-xs text-slate-500" data-testid="zoom-level-label">{zoom}%</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom((z) => Math.min(200, z + 10))} data-testid="zoom-in-button"><ZoomIn className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => setZoom(100)} data-testid="fit-screen-button"><Maximize2 className="h-3.5 w-3.5" /> {t("designer.fit")}</Button>
              <div className="ml-1.5 flex items-center gap-1 border-l border-slate-200 pl-2.5">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRotation((r) => (r + 270) % 360)} title={t("designer.rotateLeft")} data-testid="rotate-left-button"><RotateCcw className="h-4 w-4" /></Button>
                <span className="w-8 text-center text-xs text-slate-500" data-testid="rotation-level-label">{rotation}°</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRotation((r) => (r + 90) % 360)} title={t("designer.rotateRight")} data-testid="rotate-right-button"><RotateCw className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              {template && <span>{t("designer.pageSize", { w: template.widthMM, h: template.heightMM })}</span>}
              {template && <span>{t("designer.bleed", { b: template.bleedMM })}</span>}
              {mode === "A" && (
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={showSafeArea} onChange={(e) => setShowSafeArea(e.target.checked)} data-testid="safe-area-toggle" /> {t("designer.showSafeArea")}
                </label>
              )}
              {drawingLogo && (
                <span className="rounded-full bg-indigo-50 px-2.5 py-1 font-medium text-indigo-600" data-testid="draw-logo-hint">
                  {t("designer.drawLogoHint")}
                </span>
              )}
              {rotation !== 0 && (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-600" data-testid="rotation-hint">
                  {t("designer.rotationHint")}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center overflow-auto bhf-scrollbar p-10">
            <DesignerCanvas
              generating={generating} design={design} zoom={zoom}
              mode={mode} template={template} product={product} brand={brand} customContent={customContent} boxes={boxes}
              selection={selection} onSelectBox={handleSelectBox} onCommitBox={handleCommitBox}
              showSafeArea={showSafeArea} bleedXPct={bleedXPct} bleedYPct={bleedYPct}
              drawMode={drawingLogo} onDrawComplete={finishDrawLogo} rotation={rotation}
              spellResults={spellResults}
            />
          </div>
        </div>

        <PropertiesPanel mode={mode} selection={selection} customContent={customContent} product={product} brand={brand} template={template}
          patchField={patchField} patchNested={patchNested}
          updateAddedText={updateAddedText} removeAddedText={removeAddedText}
          updateAddedImage={updateAddedImage} removeAddedImage={removeAddedImage}
          updateOverlay={updateOverlay} removeOverlay={removeOverlay}
          uploadFile={uploadFile} uploadLogoFile={uploadLogoFile} removeBgFile={removeBgFile} patchLogoAndBox={patchLogoAndBox}
          onReplaceBackgroundFile={onReplaceBackgroundFile} spellResult={selectedSpellResult}
          onApplySpelling={applySpellFix} onApplySpellingAll={applySpellFixAll} textResetKey={textResetKey} />
      </div>
    </div>
  );
}
